import React from 'react';
import { Alert, Box, Link, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { makeT, PublicReservationLang } from '../../i18n/publicReservationI18n';

/**
 * 公開予約ページ（PublicReservation / ReservationLookup）共通のユーティリティ。
 * 言語切替 UI・言語別の日時整形・API エラーメッセージ整形をまとめる。
 * 表示文言はすべて publicReservationI18n の辞書経由（feast-fe ADR 0040）。
 */

/** 日本語 / EN の言語切替トグル */
export const LanguageToggle: React.FC<{
  lang: PublicReservationLang;
  onChange: (lang: PublicReservationLang) => void;
}> = ({ lang, onChange }) => {
  const t = makeT(lang);
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={lang}
      onChange={(_, value: PublicReservationLang | null) => {
        if (value) onChange(value);
      }}
      aria-label="language"
    >
      <ToggleButton value="ja">{t('langJa')}</ToggleButton>
      <ToggleButton value="en">{t('langEn')}</ToggleButton>
    </ToggleButtonGroup>
  );
};

/** FEAST株式会社のユーザー向け個人情報の取り扱い（feast-corp・フィードバック3） */
export const FEAST_USER_PRIVACY_URL = 'https://corp.feast.co.jp/legal/user-privacy/';

/** 住所→Googleマップ検索 URL（契約D・B7。address が空のときは呼ばない） */
export const googleMapsUrl = (address: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

/**
 * 公開予約ページ共通フッター。店舗の取り扱いとは別に、サービス提供者（FEAST株式会社）と
 * そのユーザー情報の取り扱いへのリンクを小さく明示する（フィードバック3）。
 */
export const PublicReservationFooter: React.FC<{ lang: PublicReservationLang }> = ({ lang }) => {
  const t = makeT(lang);
  return (
    <Box component="footer" sx={{ mt: 6, mb: 2, textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary">
        {t('footerServicePrefix')}
        <Link
          href={FEAST_USER_PRIVACY_URL}
          target="_blank"
          rel="noopener"
          color="inherit"
          underline="always"
        >
          {t('footerUserPrivacyLink')}
        </Link>
        {t('footerServiceSuffix')}
      </Typography>
    </Box>
  );
};

/**
 * 個人情報の匿名化・仮名化に関する注記（ADR 0058）。
 * 個人情報入力/同意付近（info ステップ）と予約確認画面（confirm ステップ）に表示する。
 * 「氏名・電話番号・メールアドレスは15日後に削除・匿名化／統計目的の識別子のみ仮名化して保持」＋
 * FEAST の予約プライバシーポリシー（/legal/user-privacy）へのリンク。同意リンクの近辺に統合する。
 */
export const AnonymizationNotice: React.FC<{ lang: PublicReservationLang }> = ({ lang }) => {
  const t = makeT(lang);
  return (
    <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        {t('anonymizationNotice')}
        <Link
          href={FEAST_USER_PRIVACY_URL}
          target="_blank"
          rel="noopener"
          color="inherit"
          underline="always"
        >
          {t('anonymizationPolicyLink')}
        </Link>
      </Typography>
    </Alert>
  );
};

const parseLocalDate = (value: string): Date =>
  // 'YYYY-MM-DD' はローカルタイムとして解釈させるため T00:00:00 を補う
  new Date(value.length === 10 ? `${value}T00:00:00` : value);

/** 'YYYY-MM-DD' → 言語別の月日表示（ja: 7月31日 / en: Jul 31） */
export const formatMonthDay = (lang: PublicReservationLang, dateStr: string): string => {
  const d = parseLocalDate(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const t = makeT(lang);
  const monthName = t('monthNames').split(',')[d.getMonth()];
  return lang === 'ja' ? `${monthName}${d.getDate()}日` : `${monthName} ${d.getDate()}`;
};

/** 'YYYY-MM-DDTHH:mm:ss' → 言語別の日時表示（ja: 2026年7月10日(金) 18:30 / en: Fri, Jul 10, 2026 18:30） */
export const formatPublicDateTime = (lang: PublicReservationLang, isoLocal: string): string => {
  const d = parseLocalDate(isoLocal);
  if (Number.isNaN(d.getTime())) return isoLocal;
  const t = makeT(lang);
  const weekday = t('weekdays').split(',')[d.getDay()];
  const monthName = t('monthNames').split(',')[d.getMonth()];
  const hh = `${d.getHours()}`.padStart(2, '0');
  const mm = `${d.getMinutes()}`.padStart(2, '0');
  return lang === 'ja'
    ? `${d.getFullYear()}年${monthName}${d.getDate()}日(${weekday}) ${hh}:${mm}`
    : `${weekday}, ${monthName} ${d.getDate()}, ${d.getFullYear()} ${hh}:${mm}`;
};

/** 表示年月（ja: 2026年7月 / en: Jul 2026）。month は 1-12。 */
export const formatYearMonth = (
  lang: PublicReservationLang,
  year: number,
  month: number,
): string => {
  const t = makeT(lang);
  const monthName = t('monthNames').split(',')[month - 1];
  return lang === 'ja' ? `${year}年${monthName}` : `${monthName} ${year}`;
};

/**
 * BE のエラーボディが JSON {"message": "..."} の場合に message を取り出す（422 のドメインエラー等）。
 * JSON でなければ raw をそのまま、空なら fallback を返す。
 */
export const parseApiErrorMessage = (
  raw: string | null | undefined,
  fallback: string,
): string => {
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.message === 'string' && parsed.message) {
      return parsed.message;
    }
  } catch {
    // JSON でないボディ（プレーンテキスト等）はそのまま表示する
  }
  return raw;
};
